import { Component, OnInit, computed, input, signal, inject, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTableModule } from "@angular/material/table";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatMenuModule } from "@angular/material/menu";
import dayjs from "dayjs";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import {
  LorenMeteringType,
  LorenMeteringTypeTranslation,
} from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { BatteryLevelComponent } from "../../../../shared/modules/loren-battery-level/components/battery-level/battery-level";
import { first, of, Observable} from "rxjs";
import { LorenMeteringDataIdsEnum } from "../../../loren-assets/enums/loren-metering-data-ids.enum";
import {
  LorenMetricCategories,
  LorenMetricKinds,
} from "../../../../shared/modules/loren-asset-card/constants/loren-metric-types.constants";
import { LorenFiltersComponent } from "../../../../shared/modules/loren-filters/loren-filters.component";
import { LorenMetricBinaryValues } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-icons";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenMetricUnits } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-units.constants";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import {
    LorenAssetUtilsService
} from "../../../loren-assets/services/loren-asset-utils-service/loren-asset-utils.service";
import {catchError, map, switchMap} from "rxjs/operators";
import {
    LorenHostOfAssetModel
} from "../../../../shared/modules/loren-dashboard-card/interfaces/loren-host-of-asset.model";
import {LorenImageModule} from "../../../../shared/pipes/loren-image/loren-image.module";
import { formatNumericValue, getPrimaryMeteringValue } from "../../../asset/metric.utils";
import {
  getFillLevelTranslationKeyFromData,
  isFillLevelSensor,
} from "../../../asset/fill-level.utils";
import { MatButton } from "@angular/material/button";
import {MeticConfig} from "../../../metric/metric";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  LorenMeteringContext,
  METERING_CONTEXT_TRANSLATION_NAMES,
} from "../../../loren-assets/enums/loren-metering-context.enum";
import { LorenMetering } from "../../../loren-assets/interfaces/loren-metering.interface";

@Component({
  selector: "loren-loren-asset-details-info-table",
  imports: [
    CommonModule,
    MatIcon,
    MatMenuModule,
    MatTableModule,
    MatTooltip,
    TranslocoPipe,
    LorenDateModule,
    BatteryLevelComponent,
    LorenFiltersComponent,
    LorenImageModule,
    MatButton,
  ],
  templateUrl: "./loren-asset-details-info-table.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-details-info-table.component.scss",
})
export class LorenAssetDetailsInfoTableComponent extends LorenBaseFeature implements OnInit {
  private transloco = inject(TranslocoService);
  private assetService = inject(LorenAssetService);

  readonly kinds = LorenMetricCategories;
  readonly updatedFilters = [
    {
      key: this.transloco.translate("alarms.last_day"),
      value: this.prepareDateForFilters(24, "hour"),
    },
    {
      key: this.transloco.translate("alarms.last_week"),
      value: this.prepareDateForFilters(7, "day"),
    },
    {
      key: this.transloco.translate("alarms.last_month"),
      value: this.prepareDateForFilters(30, "day"),
    },
    {
      key: this.transloco.translate("alarms.last_half_year"),
      value: this.prepareDateForFilters(6, "month"),
    },
  ];

  asset = input<LorenAsset>();

  activeFilters = signal<{ name?: string[]; updated?: string }>({});
  nameFilters = signal([]);
  sorted = signal<Record<string, "ascending" | "descending">>({});
  hostName = signal("");
  hostOfAssets = signal<Map<string, LorenHostOfAssetModel[]>>(new Map());

  filteredMetrics = computed(() => {
    const metrics = this.asset().metering;
    const filters = this.activeFilters()
    const sort = this.sorted();

    let res = [...metrics];

    if(filters.name && filters.name.length > 0) {
      res = res.filter(m => filters.name.some((n) => n === m.type));
    }

    if(filters.updated && filters.updated.length > 0) {
      res = res.filter((m) =>
        dayjs(m.time).isAfter(dayjs(filters.updated[0]))
      );
    }

    const [column] = Object.keys(sort);
    if(column) {
      if(sort[column] === "ascending") {
        res.sort((a, b) =>
          a[column] < b[column] ? -1 : a[column] > b[column] ? 1 : 0
        )
      } else {
        res.sort((a, b) =>
          a[column] > b[column] ? -1 : a[column] < b[column] ? 1 : 0
        )
      }
    }

    return res;
  })

  tableElData = computed(() => {
    const items = this.filteredMetrics().map((m) => ({
        id: m.id,
        type: m.type,
        name: m.meteringContext === LorenMeteringContext.STATE
          ? METERING_CONTEXT_TRANSLATION_NAMES.get(m.meteringContext)
          : LorenMeteringTypeTranslation.get(m.type),
        description: m.description,
        value: this.getValue(m),
        service:
            m.type === LorenMeteringType.GPS_TRACKER_SENSOR
                ? "Mondis Locator"
                : "Mondis Monitor",
        updated: m.time,
        deviceId: m.deviceId,
        kind: LorenMetricKinds.get(m.type),
        hasGPSCovering:
            LorenMetricKinds.get(m.type) === LorenMetricCategories.TRACKING &&
            this.getGPSCovering(m),
        image: null,
    }));
    // adds information about active devices that are within the range of PRESENCE_BEACON
    for (const [, assets] of this.hostOfAssets().entries()) {
          assets.forEach(asset => {
              items.push({
                  id: asset.asset.metering[0].id,
                  type: asset.asset.metering[0].type,
                  name: asset.asset.name,
                  description: asset?.asset?.description ? asset?.asset?.description : asset.asset.metering[0].description,
                  value: asset.asset?.name,
                  service: "Beacon",
                  updated: asset.time.toString(),
                  deviceId: undefined,
                  kind: LorenMetricKinds.get(asset.asset.metering[0].type),
                  hasGPSCovering: null,
                  image: asset.asset.image
              });
          });
      }
    return items;
  });

  displayedColumns: string[] = [
    "name",
    "description",
    "value",
    "service",
    "updated",
    "battery",
  ];

    ngOnInit() {
        const metering = this.asset().metering;

        // PRESENCE_BEACON
        metering
            .filter(m => LorenMetricKinds.get(m.type) === LorenMetricCategories.PRESENCE_BEACON)
            .forEach(m => {
                const firstValue = m.data?.[0]?.value;
                if (firstValue) {
                    this.getHostAsset(firstValue);
                }
            });

        // PRESENCE_SENSOR
        metering
            .filter(m => LorenMetricKinds.get(m.type) === LorenMetricCategories.PRESENCE_SENSOR)
            .forEach(() => {
                this.getHostOfAssets(this.asset().id);
            });

        this.nameFilters.set(this.getNameFilters());
    }

  getMetric(id: number) {
      return this.asset().metering.find((m) => m.id === id);
  }

  private getGPSCovering(metric) {
    const gpsCoverageMetering = metric.data.find(
      (x) => x.id === LorenMeteringDataIdsEnum.GPS_DATA_RELIABLE
    );
    if (metric.locationValidityPeriod && metric.time) {
      const now = new Date();
      const diff = (now.getTime() - metric.time.getTime()) / 1000;
      if (diff >= metric.locationValidityPeriod) {
        return false;
      }
    }

    if (!gpsCoverageMetering) {
      return true;
    }

    return gpsCoverageMetering?.value === "true";
  }

    getValue = (metering: LorenMetering) => {
        if (!metering || !metering.type) { return ''; }

        const translate = this.transloco.translate.bind(this.transloco);
        const data = metering.data || [];
        const firstValue = getPrimaryMeteringValue(metering) ?? '';

        switch (LorenMetricKinds.get(metering.type)) {
            case LorenMetricCategories.BINARY: {
                const presentationType = metering.meteringContext === LorenMeteringContext.STATE
                    ? LorenMeteringContext.STATE
                    : metering.type;
                return translate(
                    LorenMetricBinaryValues.get(presentationType)?.[
                        LorenAssetUtilsService.getBinaryValue(
                            metering.type,
                            firstValue,
                            metering.meteringContext
                        )
                    ] || ''
                );
            }
            case LorenMetricCategories.NUMERIC:
                if (isFillLevelSensor(metering.type)) {
                    const translationKey = getFillLevelTranslationKeyFromData(data);
                    return translationKey ? translate(translationKey) : firstValue;
                }

                return firstValue ? formatNumericValue(firstValue) + (LorenMetricUnits.get(metering.type) || '') : '';

            case LorenMetricCategories.PERCENTAGE:
              if(!firstValue) {
                return '';
              }

              return (Number(firstValue) * 100).toFixed(2) + (LorenMetricUnits.get(metering.type) ?? '');

            case LorenMetricCategories.PRESENCE_SENSOR:
                return `${data.length}`;

            case LorenMetricCategories.PRESENCE_BEACON: {
                const hostName = this.hostName();
                return hostName
                    ? `${translate('asset.in')} ${hostName}`
                    : translate('asset.out_of_range');
            }
            case LorenMetricCategories.TRACKING: {
                let lat;
                let lng;
                for (const item of data) {
                    if (item.key === 'lat') {
                        lat = item.value;
                    }
                    if (item.key === 'lng') {
                        lng = item.value;
                    }
                    if (lat !== undefined && lng !== undefined) {
                        break;
                    }
                }
                return [lat, lng].filter(Boolean).join(', ') || '';
            }
            default:
                return firstValue || '';
        }
    }

  filterName($event) {
    this.updateActiveFilters("name", $event);
  }

  filterUpdated($event) {
    this.updateActiveFilters("updated", $event);
  }

  sort(field: "name" | "updated") {
    const column = field === "name" ? "type" : "time";
    if (!this.sorted()[column] || this.sorted()[column] === "descending") {
      this.sorted.set({ [column]: "ascending" });
      return;
    } else {
      this.sorted.set({ [column]: "descending" });
      return;
    }
  }

  clearFilters() {
    this.activeFilters.set({});
    this.sorted.set({});
  }

  private updateActiveFilters(filterField, filterData) {
    const filterValue = Array.from(filterData.keys()).filter((k) =>
      filterData.get(k)
    );
    this.activeFilters.set({ [filterField]: filterValue });
  }

  private getNameFilters() {
    const nameSet = new Set([]);
    this.asset().metering.forEach((m) => nameSet.add(m.type));
    return Array.from(nameSet).map((p) => ({
      key: this.transloco.translate(LorenMeteringTypeTranslation.get(p)),
      value: p,
    }));
  }

  private prepareDateForFilters(value: number, unit: "hour" | "day" | "month") {
    return `${dayjs().subtract(value, unit).format("YYYY-MM-DD")}T00:00:00`;
  }


    /**
     * Sets `hostName` to the name of the host asset that the given asset is associated with.
     *
     * @param id - ID of the host asset.
     */
    private getHostAsset(id: number | string) {
        const numericId = Number(id);
        if (!numericId) {
            this.hostName.set(null);
            return;
        }

        this.loadAssets()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(allAssets => {
                const hostAsset = allAssets.find(x => x.id === numericId);
                this.hostName.set(hostAsset?.name ?? null);
            });
    }

    /**
     * Populates `hostOfAssets` with all assets that are within the range of the given host asset.
     *
     * @param id - ID of the host asset.
     */
    private getHostOfAssets(id: number | string) {
        const numericId = Number(id);
        if (!numericId) {
            this.hostOfAssets.set(new Map());
            return;
        }


        this.loadAssets()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(allAssets => {
                const result: Map<string, LorenHostOfAssetModel[]> = LorenAssetUtilsService.getHostOfAssets(numericId, allAssets);
                this.hostOfAssets.set(result);
            });
    }

    /**
     * Returns an observable of all assets, using the cached assets if available,
     * or fetching from the server if the cache is empty.
     */
    private loadAssets(): Observable<LorenAsset[]> {
        return this.assetService.lastCachedAssets$.pipe(
            first(),
            switchMap(cached => {
                if (cached && cached.length) {
                    return of(cached);
                }
                return this.assetService.query().pipe(
                    map(res => res.items ?? []),
                    catchError(err => {
                        console.error("Error fetching assets:", err);
                        return of([]);
                    })
                );
            })
        );
    }

    protected readonly MeticConfig = MeticConfig;
}
